/**
 * A FRISSÍTÉS GOMB KÉT HELYE (#54): a fejléc-jelzés és a sáv a felületek fölött.
 *
 * A rövid állapot a fejlécben áll („Kérés elküldve · Tensoft 15:50"); ami egy
 * egész mondatot kíván, a sávba kerül — ugyanoda, ahol a NAV-szinkron is
 * jelez. Az artboard: `docs/tervezes/design/tensoft-frissites/`.
 *
 * ⚠️ MENET KÖZBEN IS A RÉGI IDŐ LÁTSZIK. Amíg a friss adat nem érkezik meg, a
 * könyvelő a meglévővel dolgozik — a fejléc ezt mondja, nem egy ígéretet.
 */

import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/i18n/generated';
import {
  inFlight,
  isOld,
  liveNotice,
  type Outcome,
  stampTime,
  type TensoftRefreshStatus,
} from './tensoftRefresh';
import type { TensoftRefreshHook } from './useTensoftRefresh';

export function TensoftStamp({ refresh }: { refresh: TensoftRefreshHook }) {
  const { t } = useTranslation();
  const { status } = refresh;
  // VÁLASZ NÉLKÜL NINCS JELZÉS. Egy „még nincs adat" itt hazudna: nem tudjuk.
  if (status === null) return null;

  const now = Date.now();
  const request = status.request;
  const busy = inFlight(request);
  const old = !busy && isOld(status.dataAsOf, now);
  const time =
    status.dataAsOf === null
      ? t('tensoftRefresh.none')
      : t('tensoftRefresh.stamp', { time: stampTime(status.dataAsOf, now) });
  const phase = !busy
    ? null
    : request?.state === 'WAITING'
      ? t('tensoftRefresh.waiting')
      : t('tensoftRefresh.working');

  const dot = busy
    ? 'refresh-stamp__dot refresh-stamp__dot--busy'
    : status.dataAsOf === null
      ? 'refresh-stamp__dot refresh-stamp__dot--none'
      : old
        ? 'refresh-stamp__dot refresh-stamp__dot--old'
        : 'refresh-stamp__dot';

  return (
    <span className="refresh-stamp">
      <span
        className={old ? 'refresh-stamp__time refresh-stamp__time--old' : 'refresh-stamp__time'}
      >
        <span className={dot} aria-hidden="true" />
        {phase === null ? time : `${phase} · ${time}`}
      </span>
      <button
        type="button"
        className="refresh-stamp__button"
        disabled={refresh.busy}
        onClick={refresh.request}
      >
        {t('tensoftRefresh.button')} <kbd>t</kbd>
      </button>
    </span>
  );
}

export function TensoftRefreshNotice({ refresh }: { refresh: TensoftRefreshHook }) {
  const { t } = useTranslation();
  const { status, outcome } = refresh;

  if (outcome !== null) {
    return <OutcomeNotice outcome={outcome} refresh={refresh} />;
  }

  const live = liveNotice(status, Date.now());
  if (live === null) return null;

  switch (live.kind) {
    // A HALLGATÓ GÉP: a mondat megnevezi a gépet és az időpontot, hogy aki a
    // jelzést kapja, azonnal tudja, hol keresse (Viktor, 10-05).
    case 'noAgent':
      return (
        <p className="notice" role="alert">
          {t('tensoftRefresh.noAgent')}
        </p>
      );
    case 'silent':
      return (
        <p className="notice" role="alert">
          {live.lastSeenAt === null
            ? t('tensoftRefresh.silentNever', { label: live.label })
            : t('tensoftRefresh.silent', {
                label: live.label,
                at: formatDateTime(live.lastSeenAt),
              })}
        </p>
      );
    case 'waiting':
      return (
        <output className="hint">{t('tensoftRefresh.hintWaiting', { label: live.label })}</output>
      );
    case 'working':
      return (
        <output className="hint">
          {t('tensoftRefresh.hintWorking', { time: formatDateTime(live.pickedUpAt).slice(11) })}
        </output>
      );
    case 'colleague': {
      const time = formatDateTime(live.requestedAt).slice(11);
      return (
        <output className="hint">
          {live.name === null
            ? t('tensoftRefresh.colleagueUnknown', { time })
            : t('tensoftRefresh.colleague', { name: live.name, time })}
        </output>
      );
    }
    // A RÉGI ADAT SÁVJA nem nyugtázható: addig áll, amíg az adat friss nem
    // lesz. Viktor (10-05): „kell a sáv az elején, idővel lehet eltávolítani,
    // amikor már rászoknak".
    case 'old':
      return (
        <div className="notice refresh-notice">
          <p>{t('tensoftRefresh.old', { at: formatDateTime(live.dataAsOf) })}</p>
          <button type="button" disabled={refresh.busy} onClick={refresh.request}>
            {t('tensoftRefresh.now')}
          </button>
        </div>
      );
    default: {
      const exhaustive: never = live;
      throw new Error(`Ismeretlen jelzés: ${JSON.stringify(exhaustive)}`);
    }
  }
}

function OutcomeNotice({ outcome, refresh }: { outcome: Outcome; refresh: TensoftRefreshHook }) {
  const { t } = useTranslation();
  const dismiss = (
    <button type="button" onClick={refresh.dismiss}>
      {t('tensoftRefresh.dismiss')}
    </button>
  );
  const retry = (
    <button type="button" disabled={refresh.busy} onClick={refresh.request}>
      {t('tensoftRefresh.retry')}
    </button>
  );

  switch (outcome.kind) {
    case 'arrived':
      return (
        <output className="notice notice--clear refresh-notice">
          <p>
            {t('tensoftRefresh.arrived', {
              time: outcome.dataAsOf === null ? '' : stampTime(outcome.dataAsOf, Date.now()),
            })}
          </p>
          {dismiss}
        </output>
      );
    case 'expired':
    case 'failed':
      return (
        <div className="notice refresh-notice" role="alert">
          <p>
            {outcome.kind === 'expired'
              ? t('tensoftRefresh.expired')
              : t('tensoftRefresh.failed', { reason: outcome.reason ?? '' })}{' '}
            <Kept status={refresh.status} dataAsOf={outcome.dataAsOf} />
          </p>
          {retry}
          {dismiss}
        </div>
      );
    case 'sendFailed':
      return (
        <div className="notice refresh-notice" role="alert">
          <p>{t('tensoftRefresh.sendFailed')}</p>
          {retry}
          {dismiss}
        </div>
      );
    case 'forbidden':
      // AZ ÚJRAPRÓBÁLÁS ITT NEM SEGÍT — ugyanaz az érvelés, mint a NAV-szinkronnál.
      return (
        <p className="notice" role="alert">
          {t('tensoftRefresh.forbidden')}
        </p>
      );
    default: {
      const exhaustive: never = outcome;
      throw new Error(`Ismeretlen kimenet: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/** „Érvényben maradt adat: 15:50." — csak ha van érvényes adat. */
function Kept({
  status,
  dataAsOf,
}: {
  status: TensoftRefreshStatus | null;
  dataAsOf: string | null;
}) {
  const { t } = useTranslation();
  const kept = status?.dataAsOf ?? dataAsOf;
  if (kept === null) return null;
  return <>{t('tensoftRefresh.kept', { time: stampTime(kept, Date.now()) })}</>;
}
